import { Endpoint } from '@business/api/endpoint';
import { CompactUserResponse, UserResponse } from '@business/entities/account';
import { CommonService } from '@business/services';
import { FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import MFormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import JoditEditor, { Jodit } from 'jodit-pro-react';
import React, { useCallback, useMemo, useRef } from 'react';
import { FormGroup, InputProps, Label } from 'reactstrap';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FormStringInputProps } from './FormInput';
import './form_html.scss';
import { RTEUploadResponse } from '@business/entities/common';
type IJodit = any;

function getButtons(
  canned: boolean,
  media: boolean,
  minimal: boolean,
  veryMinimal: boolean,
  cannedName: string,
  openMediaModal: any,
  openCannedModal: any,
) {
  if (veryMinimal) {
    return [
      {
        group: 'font-style',
        buttons: [],
      },
      {
        group: 'list',
        buttons: [],
      },
      {
        group: 'indent',
        buttons: [],
      },
      {
        group: 'font',
        buttons: [],
      },
      {
        group: 'color',
        buttons: [],
      },
      {
        group: 'media',
        buttons: [],
      },
      {
        group: 'insert',
        buttons: [],
      },
      {
        group: 'form',
        buttons: [],
      },
      {
        group: 'other',
        buttons: [],
      },
      {
        group: 'info',
        buttons: [],
      },
    ];
  }
  if (minimal) {
    return [
      {
        group: 'font-style',
        buttons: [],
      },
      {
        group: 'list',
        buttons: [],
      },
      {
        group: 'indent',
        buttons: [],
      },
      {
        group: 'font',
        buttons: [],
      },
      {
        group: 'color',
        buttons: [],
      },
      {
        group: 'media',
        buttons: [],
      },
      {
        group: 'insert',
        buttons: [],
      },
      {
        group: 'form',
        buttons: [],
      },
      {
        group: 'other',
        buttons: [],
      },
    ];
  }
  return [
    {
      group: 'source',
      buttons: [],
    },
    {
      group: 'font-style',
      buttons: [],
    },
    {
      group: 'list',
      buttons: [],
    },
    {
      group: 'indent',
      buttons: [],
    },
    {
      group: 'font',
      buttons: [],
    },
    {
      group: 'color',
      buttons: [],
    },
    {
      group: 'media',
      buttons: [],
    },
    '\n',
    {
      group: 'state',
      buttons: [],
    },
    {
      group: 'clipboard',
      buttons: [],
    },
    {
      group: 'insert',
      buttons: [],
    },
    {
      group: 'form',
      buttons: [],
    },
    {
      group: 'history',
      buttons: [],
    },
    {
      group: 'search',
      buttons: [],
    },
    {
      group: 'other',
      buttons: [],
    },

    ...(canned
      ? [
          {
            group: 'info',
            buttons: [],
          },
          {
            name: cannedName,
            exec: (editor: IJodit) => {
              openCannedModal(editor as IJodit, editor.s.html);
              return false;
            },
          },
        ]
      : [
          {
            group: 'info',
            buttons: [],
          },
        ]),
    ...(media
      ? [
          {
            name: 'Media Links',
            exec: (editor: IJodit) => {
              openMediaModal(editor as IJodit, editor.s.html);
              return false;
            },
          },
        ]
      : [
          {
            group: 'info',
            buttons: [],
          },
        ]),
  ];
}

export type FormInputProps<C extends FormControl<any>> = FormStringInputProps<C>;
export const __editors: Record<string, IJodit> = {};

export function FormHTML<C extends FormControl<any>>(
  props: FormInputProps<C> &
    InputProps & {
      minimal?: boolean;
      veryMinimal?: boolean;
      full?: boolean;
      theme?: string;
      minHeight?: number;
      maxHeight?: number;
      maxWidth?: number;
      canned?: (text?: string) => Promise<Observable<string>>;
      cannedName?: string;
      media?: (text?: string) => Promise<Observable<string>>;
      users?: (CompactUserResponse | UserResponse)[];
      autoFocus?: boolean;
      askBeforePasteHTML?: boolean;
      spellcheck?: boolean;
    },
) {
  const {
    minimal = false,
    veryMinimal = false,
    full = false,
    canned,
    media,
    cannedName = 'Canned Responses',
    autoFocus = false,
    askBeforePasteHTML = true,
  } = props;
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldValue = R.toString(fieldState.value) || '';

  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || false;

  const fieldPlaceholder = fieldConfig.placeholder || '';

  const handleChangeText = useCallback(
    (value: string) => {
      formControl.setValue(value);
    },
    [formControl],
  );

  const openCannedModal = useCallback(
    (editor: IJodit, initialHTML: string) => {
      editor.selection.save(true);

      canned!(R.isEmpty(initialHTML) ? undefined : initialHTML).then(obs => {
        obs
          .pipe(
            tap(text => {
              setTimeout(() => {
                __editors[formControl.uid].selection.insertHTML(text);
              }, 1000);
            }),
          )
          .subscribe();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const openMediaModal = useCallback(
    (editor: IJodit, initialHTML: string) => {
      editor.selection.save(true);

      media!(R.isEmpty(initialHTML) ? undefined : initialHTML).then(obs => {
        obs
          .pipe(
            tap(text => {
              setTimeout(() => {
                __editors[formControl.uid].selection.insertHTML(text);
              }, 1000);
            }),
          )
          .subscribe();
      });
    },

    [formControl.uid, media],
  );

  const handleBlur = useCallback(() => {
    formControl.onBlur();
  }, [formControl]);

  const onMention = useCallback(
    (query: string) => {
      if (!query.startsWith('@')) return [];
      if (!props.users) return [];
      return props.users
        .filter(item => CommonService.getFullName(item).toLowerCase().includes(query.toLocaleLowerCase().substring(1)))
        .map(user => ({ title: `${CommonService.getFullName(user)}`, value: user.id, id: user.id, email: user.email }));
    },
    [props.users],
  );

  const autoCompleteSettings = useMemo(() => {
    if (!props.users) return {};

    return {
      autocomplete: {
        sources: [
          {
            feed: onMention,
            insertValueRenderer: (item: any) =>
              `<span class="mention" style="color: #556ee6" data-mention="${item.id}" data-user-id="${item.id}" data-user-email="${item.email}">${item.title}</span>`,
          },
        ],
      },
    };
  }, [onMention, props.users]);
  const joditRef = useRef<Jodit | null>(null);

  // @ts-ignore
  const config: IJodit['options'] = useMemo(() => {
    const headers = Endpoint.dumpHeaders;
    delete headers['Content-Type'];
    delete headers['Accept'];

    return {
      ...Jodit.defaultOptions,
      license: 'M1NDC-1DHE1-C05P6-DPFOA',
      ...autoCompleteSettings,
      disablePlugins: [
        'button-generator',
        'google-maps',
        'google-search',
        'page-break',
        'finder',
        'backup',
        'highlight-signature',
        'emoji',
        'keyboard',
        'export-docs',
        'change-case',
        'show-blocks',
        'paste-code',
        'translate',
        'mobile-view',
      ],
      placeholder: fieldPlaceholder,
      ...(full ? { sizeLG: 0, sizeMD: 0, sizeSM: 0 } : {}),
      minHeight: props.minHeight || 300,
      maxHeight: props.maxHeight,
      maxWidth: props.maxWidth,
      readonly: fieldReadonly,
      enableDragAndDropFileToEditor: true,
      saveModeInStorage: true,
      enter: 'div',
      uploader: {
        // ...Jodit.defaultOptions.uploader,
        headers: headers,
        url: Endpoint.dumpUrl('@makh/files/rte-upload'),
        insertImageAsBase64URI: false,
        imagesExtensions: ['jpg', 'png', 'jpeg', 'gif'],
        format: 'json',
        defaultHandlerSuccess: function (data: RTEUploadResponse) {
          let i;
          if (data.files && data.files.length)
            if (data && data.files && data.files.length) {
              for (i = 0; i < data.files.length; i += 1) {
                if (R.get(data.isImages, i) === true) {
                  //   __editors[formControl.uid].selection.insertImage(data.baseurl + data.files[i], 250, 250, 'ss');
                  __editors[formControl.uid].selection.insertHTML(
                    `<img style="max-width: 100%" class="img-fluid" src="${data.baseurl + data.files[i]}" />`,
                  );
                } else {
                  const fileName = data.names[i];
                  const fileUrl = data.baseurl + data.files[i];

                  __editors[formControl.uid].selection.insertHTML(`<a class="rte-file-link" href="${fileUrl}" target="_blank">${fileName}</a>`);
                }
              }
            }
        },
      },
      askBeforePasteHTML: askBeforePasteHTML,
      defaultActionOnPaste: 'insert_clear_html',
      showToolip: true,
      removeButtons: minimal
        ? ['eraser', 'copyFormat', 'hr', 'print', 'preview', 'className', 'file', 'symbol', 'about', 'classSpan', 'table']
        : veryMinimal
        ? [
            'copyFormat',
            'hr',
            'print',
            'preview',
            'className',
            'symbol',
            'about',
            'classSpan',
            'table',
            'gif',
            'strikethrough',
            'eraser',
            'symbols',
            'fullsize',
          ]
        : ['copyformat', 'about', 'print', 'symbol', 'classSpan', 'superscript', 'subscript'],
      colorPickerDefaultTab: 'color',
      buttons: getButtons(!!canned, !!media, minimal, veryMinimal, cannedName, openMediaModal, openCannedModal),
      buttonsMD: getButtons(!!canned, !!media, minimal, veryMinimal, cannedName, openMediaModal, openCannedModal),
      buttonsSM: getButtons(!!canned, !!media, minimal, veryMinimal, cannedName, openMediaModal, openCannedModal),
      buttonsLG: getButtons(!!canned, !!media, minimal, veryMinimal, cannedName, openMediaModal, openCannedModal),
      theme: props.theme || 'default',
      spellcheck: props.spellcheck,
      autofocus: autoFocus,
      ...(veryMinimal && {
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
      }),
      events: {
        afterInit(instance: any) {
          __editors[formControl.uid] = instance;
        },
      },
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldPlaceholder, fieldReadonly, full, minimal, veryMinimal, props.minHeight, props.theme, autoFocus]);

  return (
    <FormGroup {...props.groupProps}>
      <Label for={formControl.uid}>{fieldConfig.label}</Label>
      <MFormControl error={!!fieldError} fullWidth>
        <JoditEditor ref={joditRef} config={config} onBlur={handleBlur} onChange={handleChangeText} value={fieldValue} />
      </MFormControl>
      {!!fieldError && <FormHelperText style={{ color: '#f44336' }}>{fieldError}</FormHelperText>}
    </FormGroup>
  );
}
